import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Home, ArrowRight, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

// Branded 404. Previously any unknown route silently redirected to "/", which
// dropped the visitor on the homepage with no explanation ("why am I here?").
// A real not-found screen — with orientation back into the product — is the
// best-in-market behaviour. Soft-200 (SPA), so we mark it noindex,follow.
const NotFoundPage = () => {
  const { t } = useTranslation();

  const links = [
    { to: "/models", label: t("nav.models", "Modèles IA") },
    { to: "/pricing", label: t("nav.pricing", "Tarifs") },
    { to: "/use-cases", label: t("nav.useCases", "Cas d'usage") },
    { to: "/blog", label: t("nav.blog", "Blog") },
  ];

  return (
    <PageLayout>
      <Helmet>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <SEO
        title={t("notFound.title", "Page introuvable")}
        description={t(
          "notFound.subtitle",
          "Le lien est peut-être cassé ou la page a été déplacée."
        )}
        canonical="/404"
      />
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 max-w-xl mx-auto text-center">
          <p className="font-heading text-7xl sm:text-8xl font-bold text-accent-gradient mb-4 tracking-tight">
            404
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            {t("notFound.heading", "Cette page n'existe pas")}
          </h1>
          <p className="text-lg text-slate-400 mb-10">
            {t(
              "notFound.subtitle",
              "Le lien est peut-être cassé ou la page a été déplacée."
            )}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link to="/">
              <Button
                size="lg"
                className="btn-glow bg-primary-strong hover:bg-primary-strong/90 text-lg px-8"
              >
                <Home className="w-5 h-5 mr-2" />
                {t("notFound.home", "Retour à l'accueil")}
              </Button>
            </Link>
          </div>

          <div>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-4">
              <Compass className="w-4 h-4" />
              {t("notFound.explore", "Ou explorez")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full glass border border-white/10 text-sm text-slate-300 hover:text-white hover:border-primary/40 transition-colors"
                >
                  {l.label}
                  <ArrowRight className="w-3.5 h-3.5 text-primary group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default NotFoundPage;
